import React, { useState } from "react";
import { FileDown, Loader } from "lucide-react";
import { useConsumerHouseMoneyTrailRecords } from "../hooks/useTreasury";
import { formatCurrencyAmount } from "../utils/formatCurrencyAmount";
import { downloadCsv } from "../utils/exportCsv";

const when = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "-");

// The ledger rows behind one line of the money trail, both sides, with the
// customer on the consumer side. Rows whose reference is on only one side are
// the ones that pull the sides apart, so they are shown first and by default alone.
function MoneyTrailRecords({ category, currency, from }) {
  const { data, isLoading, isError, error } = useConsumerHouseMoneyTrailRecords({ currency, category, from, enabled: true });
  const [onlyUnmatched, setOnlyUnmatched] = useState(true);

  if (isLoading) {
    return (
      <p className="flex gap-2 items-center py-3 text-sm text-gray-500">
        <Loader className="w-4 h-4 animate-spin" /> Loading records...
      </p>
    );
  }
  if (isError) return <p className="py-3 text-sm text-red-600">Could not load the records: {error?.response?.data?.message || error?.message}</p>;

  const unmatched = data.records.filter((r) => !r.hasCounterpart);
  const shown = onlyUnmatched ? unmatched : data.records;
  const truncated = data.consumers.total > data.consumers.shown || data.house.total > data.house.shown;

  const handleExport = () =>
    downloadCsv(
      `money-trail-records-${category.replace(/\W+/g, "-")}-${currency}-${new Date().toISOString().slice(0, 10)}.csv`,
      ["Side", "Date", "Reference", "Customer", "Email", "Business", `Effect (${currency})`, "Status", "Other side has it", "Description"],
      data.records.map((r) => [
        r.side,
        r.date,
        r.reference ?? "",
        r.customer?.name ?? "",
        r.customer?.email ?? "",
        r.business ?? "",
        r.amount,
        r.status ?? "",
        r.hasCounterpart ? "yes" : "no",
        r.description ?? "",
      ]),
    );

  return (
    <div className="py-3">
      <div className="flex flex-wrap gap-3 justify-between items-center mb-2">
        <label className="flex gap-2 items-center text-xs text-gray-600">
          <input type="checkbox" checked={onlyUnmatched} onChange={(e) => setOnlyUnmatched(e.target.checked)} />
          Only rows with no match on the other side ({unmatched.length} of {data.records.length})
        </label>
        <button
          onClick={handleExport}
          className="flex gap-2 items-center px-3 py-1.5 text-xs font-bold text-[#013941] bg-white rounded-3xl border border-[#013941] hover:bg-gray-50"
        >
          <FileDown className="w-4 h-4" />
          Export CSV
        </button>
      </div>
      <p className="mb-2 text-xs text-gray-500">
        {`Consumers: ${data.consumers.total} row${data.consumers.total === 1 ? "" : "s"}. House merchant: ${data.house.total}.`}
        {truncated && " Only the newest 200 per side are listed."} A row is matched when the same reference is on the other side.
      </p>
      {shown.length === 0 ? (
        <p className="text-sm text-gray-500">{onlyUnmatched ? "Every row has a match on the other side." : "No rows."}</p>
      ) : (
        <div className="overflow-auto max-h-96 bg-white rounded border border-gray-200">
          <table className="w-full text-xs">
            <thead>
              <tr className="sticky top-0 tracking-wider text-left text-gray-500 uppercase bg-gray-50">
                <th className="px-3 py-2 font-semibold">Side</th>
                <th className="px-3 py-2 font-semibold">When</th>
                <th className="px-3 py-2 font-semibold">Reference</th>
                <th className="px-3 py-2 font-semibold">Who</th>
                <th className="px-3 py-2 font-semibold text-right">Effect</th>
                <th className="px-3 py-2 font-semibold">Status</th>
                <th className="px-3 py-2 font-semibold">Description</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {shown.map((r, n) => (
                <tr key={`${r.side}-${r.reference}-${n}`}>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <span className={`px-2 py-0.5 rounded-full font-semibold ${r.side === "consumer" ? "bg-blue-50 text-blue-800" : "bg-purple-50 text-purple-800"}`}>
                      {r.side === "consumer" ? "Consumer" : "House"}
                    </span>
                    {!r.hasCounterpart && <span className="block mt-1 text-amber-700">no match</span>}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-gray-600">{when(r.date)}</td>
                  <td className="px-3 py-2 font-mono text-gray-700 break-all">{r.reference ?? "-"}</td>
                  <td className="px-3 py-2 text-gray-700">
                    {r.customer ? (
                      <>
                        <span className="block">{r.customer.name || "-"}</span>
                        <span className="block text-gray-500 break-all">{r.customer.email}</span>
                      </>
                    ) : (
                      r.business || "-"
                    )}
                  </td>
                  <td className={`px-3 py-2 text-right whitespace-nowrap ${r.amount < 0 ? "text-red-600" : "text-gray-900"}`}>
                    {formatCurrencyAmount(r.amount, currency)}
                  </td>
                  <td className="px-3 py-2 text-gray-700">{r.status ?? "-"}</td>
                  <td className="px-3 py-2 text-gray-600">{r.description ?? ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default MoneyTrailRecords;
